import { UserPreferenceKeys } from '@common/preferences/types';
import { useUserPreference } from '@common/preferences/userPreference';
import { Entity } from '@common/types/Entity';
import { EntityEvents } from '@common/user-analytics';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { createContext, useContextSelector } from '@prophecy/utils/react/useZustandContext';
import { getTabId } from '@prophecy/utils/session';
import { useEffect, useMemo } from 'react';
import { EqualityFn } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useImmer } from 'use-immer';

import { useMixPanel } from '../../context/mixpanel/context';
import { useUser } from '../../context/user';
import { REPLAY_CURRENT_PLAYED_STEP } from '../../utils/localstorage-keys';
import { isReplayMode, replayToken } from '../../utils/replayToken';
import { isShareMode, shareToken } from '../../utils/shareToken';
import { useOnboardingDetailReplayQuery } from './api';
import { ReplayViewMode } from './types';

export const NEW_REPLAY_ID = 'new';
const CHAT_SESSION_DELIMITER = '###';
const CHAT_IDENTIFIER_SUFFIX = 'replay_share';
type CommonReplayState = {
  isReplayPaused: boolean;
};

export const useIfOnboardingReplay = ({
  uid,
  replayShareLink,
  replayId
}: {
  uid?: number;
  replayShareLink?: string;
  replayId?: string;
}) => {
  const { hasReplayViewMode } = useReplayModeType();
  const hasDirectAccess = replayToken.hasDirectAccess;
  const { onboardingReplayDetails = [] } = useOnboardingDetailReplayQuery({
    enabled: Boolean(hasReplayViewMode && !hasDirectAccess && (uid !== undefined || replayShareLink !== undefined))
  });

  return !!onboardingReplayDetails.find(
    (detail) =>
      detail.replay?.uid === uid || detail.replay?.shareLink === replayShareLink || detail.replay?.replayId === replayId
  );
};

export const useSetReplayUserPreference = (replayUID?: number, sharelink?: string) => {
  const [replayData, setReplayPreference] = useUserPreference(UserPreferenceKeys.replay);
  const { hasReplayViewMode } = useReplayModeType();
  const replayShareLink = sharelink ?? replayData?.shareLink;
  const isOnboardingReplay = useIfOnboardingReplay({ uid: replayUID, replayShareLink });
  const { track } = useMixPanel();
  const setReplayCompletedUserPreference = usePersistentCallback(
    async (hasReplayCompleted?: boolean, currentStep?: number) => {
      track(EntityEvents[Entity.Pipeline].ide.replayCompleted, {
        hasReplayCompleted,
        endTime: new Date().getTime(),
        currentStep,
        replayShareLink,
        hasReplayViewMode
      });
      if (replayShareLink && isOnboardingReplay && hasReplayViewMode) {
        const currentReplayStep = localStorage.getItem(REPLAY_CURRENT_PLAYED_STEP);
        const hasReplay = currentReplayStep === undefined;
        let stepIndex = currentStep ?? 0;
        if (currentStep === undefined && !hasReplay) {
          stepIndex = Number(currentReplayStep);
        }
        setReplayPreference(hasReplayCompleted || hasReplay ? null : { shareLink: replayShareLink, step: stepIndex });
      }
    }
  );

  const skipOnboarding = usePersistentCallback(() => {
    if (replayShareLink) {
      setReplayPreference(null);
    }
  });
  return { setReplayCompletedUserPreference, skipOnboarding, isOnboardingReplay };
};

export const useReplayModeType = () => {
  const [searchParams] = useSearchParams();

  const isOnboardingTour = searchParams.get('onboardingTour') === 'true';
  const isSnapshotMode = searchParams.get('snapshotMode') === 'true';
  const replayId = searchParams.get('replayId') || '';
  const shareId = searchParams.get('shareId') || '';
  const isNewReplay = replayId === NEW_REPLAY_ID;
  const hasReplayId = Boolean(replayId);
  const replayMode = searchParams.get('replayMode') as ReplayViewMode;
  const isSharedProject = Boolean(isShareMode() || shareId);
  const isReplayShareMode = ReplayViewMode.share === replayMode;
  const isReplayViewMode = ReplayViewMode.view === replayMode;
  const isReplayEditMode = ReplayViewMode.edit === replayMode;
  const isGuestUserLoggedIn = Boolean(shareToken.loggedInUserId || replayToken.loggedInUserID);

  const hasReplayShareMode = isSharedProject || (isReplayShareMode && hasReplayId);
  const hasReplayEditMode = isReplayEditMode && hasReplayId;

  let replayModeType: ReplayViewMode | undefined;
  if (isSharedProject) {
    replayModeType = ReplayViewMode.share;
  } else if (isReplayMode() && hasReplayId) {
    replayModeType = ReplayViewMode.view;
  }
  const userId = useUser()?.user?.id;
  const replaySessionChatKey =
    (replayId ?? shareId ?? 'replay_share') + CHAT_SESSION_DELIMITER + userId + CHAT_SESSION_DELIMITER + getTabId();

  const setReplaySessionChatId = usePersistentCallback((chatId: string) => {
    const chatSessionId = `${chatId}${CHAT_SESSION_DELIMITER}${CHAT_IDENTIFIER_SUFFIX}${userId}${CHAT_SESSION_DELIMITER}${getTabId()}`;
    localStorage.setItem(replaySessionChatKey, chatSessionId);
    return chatSessionId;
  });

  const getReplaySessionChatId = usePersistentCallback(() => {
    return localStorage.getItem(replaySessionChatKey);
  });

  const hasReplayViewMode = (isReplayViewMode && hasReplayId) || isReplayMode();
  const hasReplayReadOnlyMode = hasReplayShareMode || hasReplayViewMode || isSnapshotMode;

  // if shared project and replay's share view. --> persist = false, transformEnable = false
  let persist = true;
  let transformEnable = true;
  if ((hasReplayReadOnlyMode || isSnapshotMode) && !isOnboardingTour) {
    persist = false;
    transformEnable = false;
  }
  const chatNonPersistentMode = !persist || !transformEnable;

  return useMemo(
    () => ({
      hasReplayReadOnlyMode,
      replayModeType,
      isNewReplay,
      isReplayShareMode,
      isSnapshotMode,
      replayId,
      replaySessionChatKey,
      hasReplayMode: hasReplayId || isSharedProject,
      isSharedProject,
      hasReplayEditMode,
      replayMode,
      hasReplayShareMode,
      hasReplayViewMode,
      isGuestUserLoggedIn,
      actions: { getReplaySessionChatId, setReplaySessionChatId },
      AiChatV4: { persist, transformEnable, chatNonPersistentMode }
    }),
    [
      hasReplayReadOnlyMode,
      replayModeType,
      hasReplayId,
      isNewReplay,
      isReplayShareMode,
      isSnapshotMode,
      replayId,
      replaySessionChatKey,
      isSharedProject,
      hasReplayEditMode,
      replayMode,
      hasReplayShareMode,
      hasReplayViewMode,
      isGuestUserLoggedIn,
      getReplaySessionChatId,
      setReplaySessionChatId,
      persist,
      transformEnable,
      chatNonPersistentMode
    ]
  );
};

export const useCommonReplaySessionType = () => {
  const { hasReplayViewMode, replayId } = useReplayModeType();
  const [state, setState] = useImmer<CommonReplayState>({ isReplayPaused: hasReplayViewMode });
  const { track } = useMixPanel();
  const toggleReplayPaused = usePersistentCallback((isPaused?: boolean) => {
    track(EntityEvents[Entity.Pipeline].ide.replayPause, { replayId, isPaused });
    setState((draft) => {
      draft.isReplayPaused = Boolean(isPaused);
    });
  });

  useEffect(() => {
    if (!replayId && state.isReplayPaused) {
      toggleReplayPaused(false);
    }
  }, [replayId, toggleReplayPaused, state.isReplayPaused]);

  return useMemo(
    () => ({
      state,
      actions: { toggleReplayPaused }
    }),
    [toggleReplayPaused, state]
  );
};

export type CommonReplaySessionContextType = ReturnType<typeof useCommonReplaySessionType>;

export const { Context: CommonReplaySessionContext, Provider: CommonReplaySessionContextProvider } =
  createContext<CommonReplaySessionContextType>({} as CommonReplaySessionContextType);

export function CommonReplaySessionProvider({ children }: { children: React.ReactNode }) {
  const context = useCommonReplaySessionType();
  return <CommonReplaySessionContextProvider value={context}>{children}</CommonReplaySessionContextProvider>;
}

export function useCommonReplaySession<Selected>(
  selector: (value: CommonReplaySessionContextType) => Selected,
  equalityFn?: EqualityFn<Selected>
): Selected;
export function useCommonReplaySession(): CommonReplaySessionContextType;

export function useCommonReplaySession(
  selector = (state: CommonReplaySessionContextType) => state,
  equalityFn?: EqualityFn<CommonReplaySessionContextType>
) {
  return useContextSelector(CommonReplaySessionContext, selector, equalityFn);
}
